// eslint-disable-next-line import/no-cycle
import Customer from '@models/customer';
import InvoiceItem from '@models/invoice-item';
// eslint-disable-next-line import/no-cycle
import InvoiceTransaction from '@models/invoice-transaction';
import Model from '@models/model';

export const INVOICE_STATUS_DRAFT = 'Draft';
export const INVOICE_STATUS_PAID = 'Paid';
export const INVOICE_STATUS_OPEN = 'Open';
export const INVOICE_STATUS_RECURRING = 'Recurring';
export const INVOICE_STATUS_VOIDED = 'Voided';
export const INVOICE_STATUS_REFUNDED = 'Refunded';
export const INVOICE_STATUS_CLOSED = 'Closed';
export const INVOICE_STATUS_WRITE_OFF = 'Write Off';

export const INVOICE_STATUS_OVERDUE = 'Overdue';

export const INVOICE_TYPE_CREDIT_INVOICE = 'Credit Invoice';
export const INVOICE_TYPE_CUSTOMER_CREDIT = 'Customer Credit';
export const INVOICE_TYPE_INVOICE = 'Invoice';
export const INVOICE_TYPE_WRITE_OFF = 'Write Off';
export const INVOICE_TYPE_CREDIT_ALLOCATION = 'Credit Allocation';

export const INVOICE_DOCUMENT_TYPES = [
    INVOICE_TYPE_INVOICE,
    INVOICE_TYPE_CREDIT_INVOICE,
    INVOICE_TYPE_CUSTOMER_CREDIT,
    INVOICE_TYPE_WRITE_OFF,
];

export const INVOICE_STATUSES = [
    { id: INVOICE_STATUS_DRAFT, text: INVOICE_STATUS_DRAFT },
    { id: INVOICE_STATUS_RECURRING, text: INVOICE_STATUS_RECURRING },
    { id: INVOICE_STATUS_OPEN, text: INVOICE_STATUS_OPEN },
    { id: INVOICE_STATUS_PAID, text: INVOICE_STATUS_PAID },
    { id: INVOICE_STATUS_OVERDUE, text: INVOICE_STATUS_OVERDUE },
    { id: INVOICE_STATUS_VOIDED, text: INVOICE_STATUS_VOIDED },
];

export default class Invoice extends Model {
    static className = 'Invoice';

    index = true;

    yayPay = true;

    customer_purchase_order = {};

    customer_location = {};

    customer = new Customer({});

    billing_address_line_1 = null;

    billing_address_line_2 = null;

    billing_city = null;

    billing_state = null;

    billing_zip = null;

    transactions = [];

    items = [];

    credit_memos = [];

    status = INVOICE_STATUS_DRAFT;

    constructor(data) {
        super();
        this.setAttributes(data);
    }

    relations() {
        return {
            items: InvoiceItem,
            customer: Customer,
            transactions: InvoiceTransaction,
        };
    }

    resource() {
        return 'invoices';
    }

    isOpen() {
        return this.status === INVOICE_STATUS_OPEN;
    }

    isDraft() {
        return this.status === INVOICE_STATUS_DRAFT;
    }

    isRecurring() {
        return this.status === INVOICE_STATUS_RECURRING;
    }

    getTotal() {
        return this.items.reduce((total, item) => total + item.getTotal(), 0);
    }

    getChildrenTotal() {
        return this.items.reduce((total, item) => {
            if (item.children && item.children.length > 0) {
                return total + item.children.reduce((childTotal, child) => childTotal + child.getTotal(), 0);
            }
            return total;
        }, 0);
    }

    static getTotal(invoice) {
        return invoice.items.reduce((total, item) => total + InvoiceItem.getTotal(item), 0);
    }

    getTaxTotal() {
        return this.items.reduce((total, item) => total + item.tax_amount, 0);
    }

    getChildrenTaxTotal() {
        return this.items.reduce((total, item) => {
            if (item.children && item.children.length > 0) {
                return total + item.children.reduce((childTotal, child) => childTotal + child.tax_amount, 0);
            }
            return total;
        }, 0);
    }

    getBalance() {
        return this.getTotal()
            + this.getChildrenTotal()
            + this.getTaxTotal()
            + this.getChildrenTaxTotal()
            + this.getTransactionTotal();
    }

    getTransactions() {
        return this.transactions.filter((transaction) => !INVOICE_DOCUMENT_TYPES.includes(transaction.type));
    }

    getTransactionTotal() {
        return this.getTransactions().reduce((total, transaction) => total + transaction.amount, 0);
    }

    isClosed() {
        return this.status === INVOICE_STATUS_PAID || this.status === INVOICE_STATUS_VOIDED || this.status === INVOICE_STATUS_CLOSED || this.status === INVOICE_STATUS_WRITE_OFF;
    }

    isCustomerCredit() {
        return this.type === INVOICE_TYPE_CUSTOMER_CREDIT;
    }

    isInvoice() {
        return this.type === INVOICE_TYPE_INVOICE;
    }

    isWriteOff() {
        return this.type === INVOICE_TYPE_WRITE_OFF;
    }

    hasTax() {
        return this.items.some((item) => item.tax_amount > 0);
    }

    isVoided() {
        return this.status === INVOICE_STATUS_VOIDED;
    }
}
